/**
 * Do My Chats Look Fat? — MVP Orchestrator
 *
 * Public-facing funnel flow with gated content.
 * Imports engine (shared with admin) and render modules.
 */

import { analyzeArchive } from './engine/index.js';
import { animateCounter } from './render/shared.js';
import { renderChatGPTTax, renderBloatCheck, renderJeansCard, renderXraySummary, initTaxBreakdown, initWashBreakdown } from './render/ungated.js';
import { renderQuiz, initQuiz } from './render/quiz.js';
import { renderEmailGate, initGate } from './render/gate.js';
import { renderFitnessPlans, renderFattestChats, renderGhostWaste, renderPatterns, initFixPrompts } from './render/gated.js';
import { renderOverview } from './render/footer.js';

// ============================================================
// DOM References
// ============================================================

const dropZone = document.getElementById('dropZone');
const fileInput = document.getElementById('fileInput');
const progressFill = document.getElementById('progressFill');
const progressLabel = document.getElementById('progressLabel');
const resultsContainer = document.getElementById('results-container');
const resetBtn = document.getElementById('resetBtn');
const errorToast = document.getElementById('error-toast');
const errorMessage = document.getElementById('errorMessage');
const errorDismiss = document.getElementById('errorDismiss');

// ============================================================
// State Management
// ============================================================

let currentResult = null;

function setState(state) {
  document.body.dataset.state = state;
  if (state === 'results') {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }
}

// ============================================================
// Upload Handling
// ============================================================

dropZone.addEventListener('click', () => fileInput.click());

dropZone.addEventListener('dragover', (e) => {
  e.preventDefault();
  dropZone.classList.add('dragover');
});

dropZone.addEventListener('dragleave', () => {
  dropZone.classList.remove('dragover');
});

dropZone.addEventListener('drop', (e) => {
  e.preventDefault();
  dropZone.classList.remove('dragover');
  if (e.dataTransfer.files.length) handleFile(e.dataTransfer.files[0]);
});

fileInput.addEventListener('change', () => {
  if (fileInput.files.length) handleFile(fileInput.files[0]);
});

resetBtn.addEventListener('click', () => {
  fileInput.value = '';
  resultsContainer.innerHTML = '';
  resultsContainer.classList.remove('unlocked');
  currentResult = null;
  sessionStorage.removeItem('fatchat-result');
  setState('upload');
});

errorDismiss.addEventListener('click', () => {
  errorToast.hidden = true;
});

async function handleFile(file) {
  setState('processing');
  progressFill.style.width = '0%';
  progressLabel.textContent = 'Starting...';

  try {
    const result = await analyzeArchive(file, {
      onProgress: (e) => {
        let overall = 0;
        if (e.stage === 'unzip') overall = e.pct * 0.3;
        else if (e.stage === 'parse') overall = 30 + e.pct * 0.2;
        else if (e.stage === 'analyze') overall = 50 + e.pct * 0.5;

        progressFill.style.width = `${Math.round(overall)}%`;
        const label = e.detail ? `${e.detail}` : `${e.stage} ${e.pct}%`;
        progressLabel.textContent = label;
      },
    });

    currentResult = result;
    cacheResult(result);
    renderResults(result);
    setState('results');
  } catch (err) {
    setState('upload');
    errorMessage.textContent = err.message;
    errorToast.hidden = false;
  }
}

// ============================================================
// Result Cache (survives reload, clears on tab close)
// ============================================================

function cacheResult(result) {
  try {
    sessionStorage.setItem('fatchat-result', JSON.stringify(result, (key, val) => {
      if (val === Infinity) return '__Infinity__';
      return val;
    }));
  } catch (_) { /* quota exceeded — silently skip */ }
}

function loadCachedResult() {
  try {
    const raw = sessionStorage.getItem('fatchat-result');
    if (!raw) return null;
    return JSON.parse(raw, (key, val) => {
      if (val === '__Infinity__') return Infinity;
      return val;
    });
  } catch (_) { return null; }
}

// Auto-restore on page load
const cached = loadCachedResult();
if (cached) {
  currentResult = cached;
  renderResults(cached);
  setState('results');
}

// ============================================================
// Results Rendering — Funnel Flow
// ============================================================

function renderResults(result) {
  resultsContainer.innerHTML = [
    renderChatGPTTax(result),      // 1. Hero (ungated)
    renderBloatCheck(result),      // 2. The Weigh-In (ungated)
    renderJeansCard(result),       // 3. Jeans Card + share buttons (ungated)
    renderXraySummary(result),     // 4. X-Ray headline stat only (ungated)
    renderQuiz(result),            // 5. "Which Chat Is the Fattest?" (ungated)
    renderEmailGate(result),       // 6. Email capture (ungated)
    renderFitnessPlans(result),    // 7. 5 fix categories (GATED)
    renderFattestChats(result),    // 8. Top 10 detail table (GATED)
    renderGhostWaste(result),      // 9. The Void detail (GATED)
    renderPatterns(result),        // 10. Cross-chat patterns (GATED)
    renderOverview(result),        // 11. Archive overview (ungated footer)
  ].join('');

  initAnimations();
  initTaxBreakdown();
  initWashBreakdown();
  initQuiz();
  initGate();
  initFixPrompts();
  initShareCard(result);
}

// ============================================================
// Animations
// ============================================================

function initAnimations() {
  // Counter animation for hero number
  const heroEl = document.querySelector('[data-counter]');
  if (heroEl) {
    const target = parseFloat(heroEl.dataset.counter);
    animateCounter(heroEl, target);
  }

  // Intersection observer for section reveals
  const observer = new IntersectionObserver((entries) => {
    for (const entry of entries) {
      if (entry.isIntersecting) {
        entry.target.classList.add('visible');
        observer.unobserve(entry.target);
      }
    }
  }, { threshold: 0.08, rootMargin: '0px 0px -40px 0px' });

  document.querySelectorAll('.section:not(.visible)').forEach((el) => {
    observer.observe(el);
  });

  // Make the first section visible immediately
  const firstSection = document.querySelector('.section');
  if (firstSection) {
    firstSection.classList.add('visible');
  }
}

// ============================================================
// Share Card: Canvas API (1200x628 social image)
// ============================================================

function initShareCard(result) {
  const downloadBtn = document.getElementById('btn-download-card');
  const challengeBtn = document.getElementById('btn-challenge');

  if (downloadBtn) {
    downloadBtn.addEventListener('click', () => generateShareCard(result));
  }

  if (challengeBtn) {
    challengeBtn.addEventListener('click', () => copyChallenge(result));
  }
}

function generateShareCard(result) {
  const total = result.totalConversations || 1;
  const avgTokens = result.totalTokens / total;
  const avgMessages = result.totalMessages / total;
  const tokenSignal = Math.min(1, avgTokens / 70000);
  const messageSignal = Math.min(1, avgMessages / 80);
  const cardScore = tokenSignal * 0.6 + messageSignal * 0.4;
  const bloatPct = Math.round(cardScore * 100);
  const hours = result.chatgptTax.taxPaid.totalHours;
  const tier = getShareTier(cardScore);

  const img = new Image();
  img.onload = () => {
    const canvas = document.createElement('canvas');
    canvas.width = 2400;
    canvas.height = 1256;
    const ctx = canvas.getContext('2d');

    // White background
    ctx.fillStyle = '#FFFFFF';
    ctx.fillRect(0, 0, 2400, 1256);

    // Jeans image — left 40%
    const imgArea = 960;
    const scale = Math.min(imgArea / img.width, 1256 / img.height);
    const imgW = img.width * scale;
    const imgH = img.height * scale;
    ctx.drawImage(img, (imgArea - imgW) / 2, (1256 - imgH) / 2, imgW, imgH);

    // Right side text — vertically centered in canvas
    const textX = 1060;
    const ty = 278; // vertical offset to center 700px text block in 1256px

    // Brand
    ctx.font = 'bold 38px "Bricolage Grotesque", sans-serif';
    ctx.fillStyle = '#5A4A3A';
    ctx.textAlign = 'left';
    ctx.fillText('DO MY CHATS LOOK FAT?', textX, ty);

    // Hero percentage
    ctx.font = 'bold 220px "Fira Code", monospace';
    ctx.fillStyle = '#1B2838';
    ctx.fillText(`${bloatPct}%`, textX, ty + 300);

    // BLOATED label
    ctx.font = 'bold 48px "Oswald", sans-serif';
    ctx.fillStyle = '#C0392B';
    ctx.fillText('BLOATED', textX, ty + 370);

    // Stats
    ctx.font = '36px "Fira Code", monospace';
    ctx.fillStyle = '#5A7088';
    ctx.fillText(`${total} chats`, textX, ty + 480);
    ctx.fillText(`${result.totalMessages.toLocaleString()} messages`, textX, ty + 530);
    ctx.fillText(`${hours.toFixed(1)} hrs lost`, textX, ty + 580);

    // Fit label
    ctx.font = 'bold 44px "Oswald", sans-serif';
    ctx.fillStyle = '#2A3F55';
    ctx.fillText(tier.fit.toUpperCase(), textX, ty + 700);

    // Watermark
    ctx.font = '28px "Fira Code", monospace';
    ctx.fillStyle = 'rgba(90,112,136,0.4)';
    ctx.textAlign = 'right';
    ctx.fillText('domychatslookfat.com', 2340, 1210);

    // Download
    canvas.toBlob((blob) => {
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'my-chat-fitness.png';
      a.click();
      URL.revokeObjectURL(url);
    }, 'image/png');
  };
  img.src = tier.img;
}

function copyChallenge(result) {
  const total = result.totalConversations || 1;
  const avgTokens = result.totalTokens / total;
  const avgMessages = result.totalMessages / total;
  const tokenSignal = Math.min(1, avgTokens / 70000);
  const messageSignal = Math.min(1, avgMessages / 80);
  const cardScore = tokenSignal * 0.6 + messageSignal * 0.4;
  const bloatPct = Math.round(cardScore * 100);

  const text = `My ChatGPT chats are ${bloatPct}% bloated. Think yours are leaner? domychatslookfat.com`;

  navigator.clipboard.writeText(text).then(() => {
    const btn = document.getElementById('btn-challenge');
    if (btn) {
      const original = btn.textContent;
      btn.textContent = 'Copied!';
      setTimeout(() => { btn.textContent = original; }, 2000);
    }
  });
}

function getShareTier(cardScore) {
  if (cardScore < 0.15) return { fit: 'Loose Fit', img: 'img/tier-1-loose-fit-app.jpg' };
  if (cardScore < 0.35) return { fit: 'Snug', img: 'img/tier-2-snug-app.jpg' };
  if (cardScore < 0.55) return { fit: 'Straining', img: 'img/tier-3-straining-app.jpg' };
  if (cardScore < 0.80) return { fit: 'Splitting', img: 'img/tier-4-splitting-app.jpg' };
  return { fit: 'Blown Out', img: 'img/tier-5-blown-out-app.jpg' };
}
